import {
    Divider,
    BreadcrumbLink,
    Breadcrumb,
    Heading,
    HStack,
    Text,
    VStack,
    Wrap,
    BreadcrumbItem,
    Image,
    Box,
    StackDivider,
    Tag,
    Link as ChakraLink,
    Icon,
} from '@chakra-ui/react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Graphics } from '../../../../../config/svg';
import { VAROS_YELLOW } from '../../.././../../config';
import { ReportingViewBaseConfig } from '../../../base';
import { ContentOverlayViewProps } from './contentOverlayProps';
import { RiDoubleQuotesL, RiDoubleQuotesR } from 'react-icons/ri';
import React from 'react';
import { extractAndRenderNodes, NodeRenderers } from './contentOverlayHelper';
import { Element as HastElement, Text as HastText } from 'hast';

export function createContentOverlayView(
    config: ReportingViewBaseConfig
): React.FC<ContentOverlayViewProps> {
    const {
        UI: {
            Application: { Anchor },
        },
    } = config;

    // Define the specific renderers using Chakra components
    const contentRenderers: NodeRenderers = {
        text: (text, key) => <React.Fragment key={key}>{text}</React.Fragment>,
        strong: (children, key) => (
            <Text as="span" fontWeight="semibold" key={key}>
                {children}
            </Text>
        ),
        emphasis: (children, key) => (
            <Text as="span" fontStyle="italic" key={key}>
                {children}
            </Text>
        ),
        link: (children, href, key) => (
            <ChakraLink
                href={href}
                color="blue.300"
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                {children}
            </ChakraLink>
        ),
        ul: (children, key) => (
            <VStack key={key} spacing={2} alignItems="start" pl={4}>
                {children}
            </VStack>
        ),
        li: (children, key) => (
            <HStack key={key} spacing={3} alignItems="start" color="whiteAlpha.700">
                <Text>•</Text>
                <VStack alignItems="start" spacing={1}>
                    {children}
                </VStack>
            </HStack>
        ),
    };

    const markdownComponents: Components = {
        h1: (props) => (
            <Heading
                as="h1"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="2xl"
                mt={10}
                mb={2}
                {...props}
            />
        ),
        h2: (props) => (
            <Heading
                as="h2"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="xl"
                mt={8}
                mb={2}
                {...props}
            />
        ),
        h3: (props) => (
            <Heading
                as="h3"
                color="whiteAlpha.900"
                fontWeight="semibold"
                fontSize="lg"
                mt={6}
                my={2}
                {...props}
            />
        ),
        p: (props) => {
            const { children } = props;
            return (
                <Text m={0} color="whiteAlpha.700" {...props}>
                    {children}
                </Text>
            );
        },
        a: (props) => {
            const { children, href } = props;
            return (
                <ChakraLink href={href} color="blue.300" target="_blank" {...props}>
                    {children}
                </ChakraLink>
            );
        },
        blockquote: (props) => {
            const { node } = props;
            const childrenNodes = node?.children as
                | (HastElement | HastText)[]
                | undefined;
            // Pass the renderers to the helper function
            const renderedContent = extractAndRenderNodes(
                childrenNodes,
                'blockquote',
                contentRenderers // Ensure 3rd argument is present
            );

            return (
                <Box
                    as="blockquote"
                    bg="whiteAlpha.50"
                    borderRadius="md"
                    mt={4}
                    p={4}
                    {...props}
                >
                    <Text
                        fontStyle="italic"
                        color="whiteAlpha.800"
                        fontSize="lg"
                        fontWeight="semibold"
                    >
                        <Icon
                            as={RiDoubleQuotesL}
                            display="inline"
                            fontSize="2xl"
                            color={VAROS_YELLOW[100]}
                            mr={2}
                        />
                        {/* Directly render the nodes returned by the helper */}
                        {renderedContent}
                        {/* <Icon
                            ml={2}
                            as={RiDoubleQuotesR}
                            display="inline"
                            fontSize="2xl"
                            color={VAROS_YELLOW[100]}
                        /> */}
                    </Text>
                </Box>
            );
        },
        ul: (props) => {
            const { node, children } = props;
            return (
                <VStack spacing={2} alignItems="start" my={4}>
                    {children}
                </VStack>
            );
        },
        li: (props) => {
            const { node } = props;
            const childrenNodes = node?.children as
                | (HastElement | HastText)[]
                | undefined;
            // Pass the renderers to the helper function
            const renderedContent = extractAndRenderNodes(
                childrenNodes,
                'li',
                contentRenderers // Ensure 3rd argument is present
            );

            return (
                <HStack spacing={3} alignItems="start" color="whiteAlpha.700">
                    <Text>•</Text>
                    <VStack alignItems="start" spacing={1}>
                        {/* Directly render the nodes returned by the helper */}
                        {renderedContent}
                    </VStack>
                </HStack>
            );
        },
        hr: (props) => {
            return (
                <Divider
                    {...props}
                    mt={6}
                    borderTopWidth={2}
                    borderColor="whiteAlpha.200"
                />
            );
        },
    };

    return (props) => {
        return (
            <HStack
                w="full"
                py={{ base: 8, lg: 16 }}
                pb={{ base: 16, lg: 32 }}
                justify="center"
            >
                <VStack
                    maxW="48rem"
                    w="full"
                    align="start"
                    spacing={{ base: 8, lg: 12 }}
                    divider={
                        <StackDivider borderColor="whiteAlpha.200" borderWidth={1} />
                    }
                >
                    <VStack align="start" w="full" spacing={8}>
                        <Breadcrumb spacing={4}>
                            {props.breadcrumb.items.map((item) => (
                                <BreadcrumbItem
                                    key={item.id}
                                    isCurrentPage={item.isActive}
                                    color="whiteAlpha.400"
                                >
                                    <BreadcrumbLink
                                        maxW="24rem"
                                        isTruncated={true}
                                        aria-selected={item.isActive}
                                        _selected={{
                                            color: 'whiteAlpha.600',
                                            cursor: 'default',
                                            _hover: {
                                                color: 'whiteAlpha.600',
                                                cursor: 'default',
                                            },
                                        }}
                                    >
                                        {item.label}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                            ))}
                        </Breadcrumb>
                        <Image src={Graphics.Brand.WhiteText} h={8} w="auto" />
                        <Heading fontSize="4xl" fontWeight="semibold">
                            {props.content.title}
                        </Heading>
                        <HStack
                            spacing={3}
                            color="whiteAlpha.600"
                            divider={<Box border="none">&#x2022;</Box>}
                        >
                            <Text>{props.content.timestamp.absolute}</Text>
                            <Text>{props.content.author.label}</Text>
                            <Text>{props.content.duration.label}</Text>
                        </HStack>
                        <Wrap shouldWrapChildren={true} spacing={3}>
                            <Tag size="lg" borderRadius="sm" colorScheme="blue">
                                Case study
                            </Tag>
                        </Wrap>
                        <Box
                            lineHeight={1.6}
                            sx={{
                                '& > *:first-child:is(h1, h2, h3, h4, h5, h6)': {
                                    marginTop: 0,
                                },
                                '& blockquote + *': {
                                    marginTop: 4,
                                },
                                '& .chakra-divider + *': {
                                    marginTop: 4,
                                },
                            }}
                        >
                            <ReactMarkdown
                                components={markdownComponents}
                                children={props.content.markdown}
                                skipHtml={true}
                            />
                        </Box>
                    </VStack>
                    <VStack align="start" w="full">
                        <HStack>
                            <Text fontWeight="semibold" fontSize="xl">
                                References
                            </Text>
                        </HStack>
                        <VStack align="start" w="full" spacing={4}>
                            {props.references.map((reference) => {
                                const content = (
                                    <HStack
                                        spacing={2}
                                        align="baseline"
                                        color="whiteAlpha.600"
                                    >
                                        <Text flexShrink={0}>{reference.number}.</Text>
                                        <HStack spacing={1} wrap="wrap" align="baseline">
                                            {reference.author ? (
                                                <Text>{reference.author},</Text>
                                            ) : null}
                                            {reference.year ? (
                                                <Text>({reference.year})</Text>
                                            ) : null}
                                            <Text
                                                fontStyle="italic"
                                                maxW="20rem"
                                                isTruncated={true}
                                            >
                                                {reference.label}
                                            </Text>
                                            {reference.publisher ? (
                                                <Text>{reference.publisher}</Text>
                                            ) : null}
                                        </HStack>
                                    </HStack>
                                );
                                if (reference.anchor) {
                                    return (
                                        <HStack key={reference.id}>
                                            <Anchor
                                                {...reference.anchor}
                                                target="_blank"
                                                _hover={{
                                                    textDecoration: 'underline',
                                                    textDecorationColor: 'whiteAlpha.600',
                                                }}
                                            >
                                                {content}
                                            </Anchor>
                                        </HStack>
                                    );
                                }
                                return <HStack key={reference.id}>{content}</HStack>;
                            })}
                        </VStack>
                    </VStack>
                </VStack>
            </HStack>
        );
    };
}
